import { useForm, useList, useOne } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { ContentTagsField, LinkTargetSelect } from '@/components/shared';
import { FormField, FormHeader, FormSpacer, FormStickyActions } from '@/components/forms';
import { FileImageUpload } from '@/components/upload/FileImageUpload';
import { LanguageTabs } from '@/components/universal';
import {
  Settings,
  Image as ImageIcon,
  Link as LinkIcon,
  Target,
  Plus,
  Trash2,
} from 'lucide-react';
import { MiniBannerTargetType, type LivestreamStatus } from '@masters-club/types';
import { LIVESTREAM_STATUS_META } from '../livestreams/types';
import {
  TARGET_TYPE_LABELS,
  MINI_BANNER_DEVICE_SLOTS,
  MINI_BANNER_DEFAULT_ASPECT_RATIO,
  type MiniBanner,
  type MiniBannerFormData,
  type MiniBannerImageInput,
  type MiniBannerTargetInput,
} from './types';

// ============================================
// Types
// ============================================

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

interface LanguageTabItem {
  code: string;
  label: string;
  flagEmoji?: string;
}

interface CourseRow {
  id: number;
  slug: string;
  title?: string;
  translations?: Array<{ title?: string; languageCode?: string }>;
}

interface ModuleItemInCourse {
  id: number;
  moduleId: number;
  order: number;
  type: string;
  translations?: Array<{ title?: string; languageCode?: string }>;
}

interface ModuleInCourse {
  id: number;
  courseId: number;
  order: number;
  translations?: Array<{ title?: string; languageCode?: string }>;
  items?: ModuleItemInCourse[];
}

interface CourseWithModules extends CourseRow {
  modules?: ModuleInCourse[];
}

interface LivestreamRow {
  id: number;
  title?: string;
  status?: LivestreamStatus;
  scheduledAt?: string | null;
  translations?: Array<{ title?: string; languageCode?: string }>;
}

/**
 * Слот изображения — комбинация (язык × соотношение сторон).
 * languageCode=null — универсальный; aspectRatio — '19:2' десктоп / '21:9' моб.
 */
interface ImageSlot {
  languageCode: string | null;
  aspectRatio: string;
  imageId: number | null;
  imageUrl: string;
}

// ============================================
// Helpers
// ============================================

function getTargetLabel(target: MiniBannerTargetInput): string {
  if (target.targetId === 0) { return 'новая цель'; }
  if (target.targetType === MiniBannerTargetType.LIVESTREAM) { return `Трансляция #${target.targetId}`; }
  if (target.targetType === MiniBannerTargetType.MODULE_ITEM) { return `Урок #${target.targetId}`; }
  if (target.targetType === MiniBannerTargetType.MODULE) { return `Модуль #${target.targetId}`; }
  return `Курс #${target.targetId}`;
}

function getRuTitle(translations?: Array<{ title?: string; languageCode?: string }>): string | null {
  if (!translations || translations.length === 0) { return null; }
  const ru = translations.find((t) => t.languageCode === 'ru');
  return ru?.title ?? translations[0]?.title ?? null;
}

function getLivestreamDisplayName(stream: LivestreamRow): string {
  const title = getRuTitle(stream.translations) ?? stream.title ?? `#${stream.id}`;
  const statusLabel = stream.status ? LIVESTREAM_STATUS_META[stream.status].label : null;
  return statusLabel ? `${title} · ${statusLabel}` : title;
}

/** На каждый язык (или универсально) — по слоту на каждый aspectRatio из MINI_BANNER_DEVICE_SLOTS. */
function buildImageSlots(
  existingImages: MiniBannerImageInput[],
  languages: LanguageTabItem[] | undefined,
  localizationEnabled: boolean,
): ImageSlot[] {
  const slots: ImageSlot[] = [];

  const pushSlotsFor = (languageCode: string | null) => {
    for (const device of MINI_BANNER_DEVICE_SLOTS) {
      const existing = existingImages.find(
        (img) =>
          (img.languageCode ?? null) === languageCode &&
          (img.aspectRatio ?? MINI_BANNER_DEFAULT_ASPECT_RATIO) === device.aspectRatio,
      );
      slots.push({
        languageCode,
        aspectRatio: device.aspectRatio,
        imageId: existing?.imageId ?? null,
        imageUrl: existing?.imageUrl ?? '',
      });
    }
  };

  if (localizationEnabled && languages && languages.length > 0) {
    for (const lang of languages) { pushSlotsFor(lang.code); }
  } else {
    pushSlotsFor(null);
  }

  return slots;
}

function slotsToImageInputs(slots: ImageSlot[]): MiniBannerImageInput[] {
  return slots
    .filter((s): s is ImageSlot & { imageId: number } => s.imageId !== null)
    .map((s) => ({
      imageId: s.imageId,
      imageUrl: s.imageUrl,
      aspectRatio: s.aspectRatio,
      languageCode: s.languageCode,
    }));
}

// ============================================
// Main Form
// ============================================

export function MiniBannersForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<MiniBanner>({
    resource: 'mini-banners',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  const languages: LanguageTabItem[] | undefined = languagesResult?.data?.map((l) => ({
    code: l.code,
    label: l.nativeName || l.name,
    flagEmoji: l.flagEmoji || l.flag || undefined,
  }));

  const banner = query?.data?.data;
  const formKey = isEdit ? `edit-${banner?.id ?? 'loading'}` : 'create';

  return (
    <MiniBannerFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      banner={banner}
      onFinish={async (v) => {
        await onFinish(v);
      }}
      onBack={() => navigate(-1)}
      languages={languages}
    />
  );
}

// ============================================
// Form Fields
// ============================================

interface FormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  banner?: MiniBanner;
  onFinish: (values: MiniBannerFormData) => Promise<void>;
  onBack: () => void;
  languages?: LanguageTabItem[];
}

function MiniBannerFormFields({
  isEdit,
  formLoading,
  banner,
  onFinish,
  onBack,
  languages,
}: FormFieldsProps) {
  // Dates & status
  const [startDate, setStartDate] = useState<Date | null>(
    banner?.startDate ? new Date(banner.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    banner?.endDate ? new Date(banner.endDate) : null,
  );
  const [isActive, setIsActive] = useState(banner?.isActive ?? true);

  // Link — общий LinkTargetSelect (как в обычных баннерах).
  const [linkUrl, setLinkUrl] = useState<string | null>(banner?.linkUrl ?? null);

  // Targets — пользователь выбирает курс, потом опционально модуль, опционально урок.
  // resolvedCourseId / resolvedModuleId приходят от backend и нужны для правильной
  // инициализации каскадных селектов при редактировании.
  const [targets, setTargets] = useState<MiniBannerTargetInput[]>(
    () =>
      banner?.targets.map((t) => ({
        targetType: t.targetType,
        targetId: t.targetId,
        resolvedCourseId: t.resolvedCourseId,
        resolvedModuleId: t.resolvedModuleId,
      })) ?? [],
  );

  // Localization
  const [localizationEnabled, setLocalizationEnabled] = useState(() => {
    const imgs = banner?.images ?? [];
    return imgs.some((img) => img.languageCode !== null);
  });
  const [activeLanguage, setActiveLanguage] = useState(languages?.[0]?.code ?? 'ru');

  // Image slots
  const existingImages: MiniBannerImageInput[] = (banner?.images ?? []).map((img) => ({
    imageId: img.imageId,
    imageUrl: img.imageUrl ?? '',
    aspectRatio: img.aspectRatio ?? MINI_BANNER_DEFAULT_ASPECT_RATIO,
    languageCode: img.languageCode,
  }));

  const [imageSlots, setImageSlots] = useState<ImageSlot[]>(() =>
    buildImageSlots(existingImages, languages, localizationEnabled),
  );

  const updateSlotImage = (index: number, fileRecord: { id: number; url: string } | null) => {
    setImageSlots((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, imageId: fileRecord?.id ?? null, imageUrl: fileRecord?.url ?? '' }
          : s,
      ),
    );
  };

  const handleLocalizationChange = (enabled: boolean) => {
    setLocalizationEnabled(enabled);
    const currentInputs = slotsToImageInputs(imageSlots);
    setImageSlots(buildImageSlots(currentInputs, languages, enabled));
  };

  // Обязателен хотя бы один десктопный (19:2) слот — без него баннер не показывается.
  const desktopAspectRatio = MINI_BANNER_DEVICE_SLOTS.find((d) => d.required)?.aspectRatio;
  const hasRequiredImage = imageSlots.some(
    (s) => s.imageId !== null && s.aspectRatio === desktopAspectRatio,
  );
  const hasValidTargets = targets.length > 0 && targets.every((t) => t.targetId > 0);

  const handleSubmit = async () => {
    const formData: MiniBannerFormData = {
      images: slotsToImageInputs(imageSlots),
      targets,
      translations: [],
      linkUrl,
      order: banner?.order ?? 0,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      isActive,
    };
    await onFinish(formData);
  };

  // Filter slots for current language tab (или один универсальный если localization off)
  const visibleSlots = localizationEnabled
    ? imageSlots
        .map((slot, originalIndex) => ({ slot, originalIndex }))
        .filter(({ slot }) => slot.languageCode === activeLanguage)
    : imageSlots.map((slot, originalIndex) => ({ slot, originalIndex }));

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? 'Редактировать мини-баннер' : 'Создать мини-баннер'}
        onBack={onBack}
        sticky
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left column — Images + Targets + Link */}
        <div className="xl:col-span-2 space-y-6">
          {/* Images Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Изображения
                  <span className="text-xs font-normal text-muted-foreground">
                    (десктоп 19:2 — обязательно, мобильный 21:9 — опционально)
                  </span>
                </span>
                {languages && languages.length > 1 && (
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="localization-toggle"
                      className="text-xs font-normal text-muted-foreground cursor-pointer"
                    >
                      Локализация
                    </Label>
                    <Select
                      value={localizationEnabled ? 'on' : 'off'}
                      onValueChange={(v) => handleLocalizationChange(v === 'on')}
                    >
                      <SelectTrigger id="localization-toggle" className="w-[100px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Выкл</SelectItem>
                        <SelectItem value="on">Вкл</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {localizationEnabled && languages && languages.length > 0 && (
                <LanguageTabs
                  languages={languages}
                  activeLanguage={activeLanguage}
                  onLanguageChange={setActiveLanguage}
                />
              )}

              <div className="space-y-4">
                {visibleSlots.map(({ slot, originalIndex }) => {
                  const device = MINI_BANNER_DEVICE_SLOTS.find(
                    (d) => d.aspectRatio === slot.aspectRatio,
                  );
                  return (
                    <div key={originalIndex} className="space-y-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <ImageIcon className="h-3.5 w-3.5" />
                        <span className="font-medium">
                          {device?.label ?? slot.aspectRatio}
                          {device?.required ? '' : ' (опционально)'}
                        </span>
                        <span>· {device?.description ?? slot.aspectRatio}</span>
                        {slot.languageCode && (
                          <span>· язык {slot.languageCode.toUpperCase()}</span>
                        )}
                      </div>
                      <FileImageUpload
                        value={slot.imageId ? { id: slot.imageId, url: slot.imageUrl } : null}
                        onChange={(fileRecord) => updateSlotImage(originalIndex, fileRecord)}
                        placeholder={slot.aspectRatio}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Targets Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Где показывать
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Баннер показывается на уроках, которые попадают под одну из целей.
                Курс — на всех уроках курса. Модуль — на уроках модуля. Урок — только на этом уроке.
              </p>

              {targets.map((t, idx) => (
                <TargetRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={`target-${idx}`}
                  target={t}
                  onChange={(next) =>
                    setTargets((prev) => prev.map((x, i) => (i === idx ? next : x)))
                  }
                  onRemove={() =>
                    setTargets((prev) => prev.filter((_, i) => i !== idx))
                  }
                />
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setTargets((prev) => [
                    ...prev,
                    { targetType: MiniBannerTargetType.COURSE, targetId: 0 },
                  ])
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Добавить цель
              </Button>
            </CardContent>
          </Card>

          {/* Link Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Ссылка
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <LinkTargetSelect value={linkUrl} onChange={setLinkUrl} />

              <p className="text-xs text-muted-foreground">
                Оставьте пустым, чтобы баннер был не кликабельным.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Right column — Settings + Translations */}
        <div className="space-y-6">
          {/* Settings Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Настройки
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Начало показа" name="startDate">
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="Без ограничения"
                />
              </FormField>

              <FormField label="Конец показа" name="endDate">
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="Без ограничения"
                />
              </FormField>

              <FormField label="Статус" name="isActive">
                <Select
                  value={isActive ? 'active' : 'inactive'}
                  onValueChange={(v) => setIsActive(v === 'active')}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Активен</SelectItem>
                    <SelectItem value="inactive">Неактивен</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </CardContent>
          </Card>

          {isEdit && banner?.id ? (
            <Card>
              <CardContent className="pt-6">
                <ContentTagsField entityType="MINI_BANNER" entityId={banner.id} />
              </CardContent>
            </Card>
          ) : null}

        </div>
      </div>

      <FormSpacer />

      <FormStickyActions>
        <Button
          onClick={handleSubmit}
          disabled={formLoading || !hasRequiredImage || !hasValidTargets}
        >
          {isEdit ? 'Сохранить' : 'Создать'}
        </Button>
        <Button variant="outline" onClick={onBack}>
          Отмена
        </Button>
      </FormStickyActions>
    </div>
  );
}

// ============================================
// Target row — cascade: course → module → lesson
// ============================================

interface TargetRowProps {
  target: MiniBannerTargetInput;
  onChange: (next: MiniBannerTargetInput) => void;
  onRemove: () => void;
}

function TargetRow({ target, onChange, onRemove }: TargetRowProps) {
  // Список курсов для первого селекта
  const { result: coursesResult } = useList<CourseRow>({
    resource: 'courses',
    pagination: { pageSize: 200, mode: 'server' },
  });

  const { result: livestreamsResult } = useList<LivestreamRow>({
    resource: 'livestreams',
    pagination: { pageSize: 200, mode: 'server' },
  });

  // Инициализируем выбранный курс:
  //  - COURSE: из самого targetId
  //  - MODULE/MODULE_ITEM: из resolvedCourseId, полученного с backend
  const [selectedCourseId, setSelectedCourseId] = useState<number>(() => {
    if (target.targetType === MiniBannerTargetType.COURSE) { return target.targetId; }
    return target.resolvedCourseId ?? 0;
  });

  // Загружаем курс со всеми его модулями и уроками — один запрос вместо трёх
  const { result: courseResult } = useOne<CourseWithModules>({
    resource: 'courses',
    id: selectedCourseId > 0 ? selectedCourseId : undefined,
    queryOptions: { enabled: selectedCourseId > 0 },
  });

  const courses = coursesResult?.data ?? [];
  const livestreams = livestreamsResult?.data ?? [];
  // В Refine 5 result от useOne — это сама запись (или undefined), без обёртки { data }.
  const courseWithModules = courseResult;
  const modules = courseWithModules?.modules ?? [];

  // Определяем какой модуль сейчас "выбран" — это либо MODULE target, либо
  // родительский модуль MODULE_ITEM target, либо первый (при смене курса).
  const currentModuleId = (() => {
    if (target.targetType === MiniBannerTargetType.MODULE) { return target.targetId; }
    if (target.targetType === MiniBannerTargetType.MODULE_ITEM) {
      const parent = modules.find((m) => m.items?.some((i) => i.id === target.targetId));
      return parent?.id ?? 0;
    }
    return 0;
  })();

  const items = modules.find((m) => m.id === currentModuleId)?.items ?? [];

  const handleCourseChange = (courseIdStr: string) => {
    const courseId = parseInt(courseIdStr, 10);
    setSelectedCourseId(courseId);
    // По умолчанию баннер на весь курс
    onChange({ targetType: MiniBannerTargetType.COURSE, targetId: courseId });
  };

  const handleModuleChange = (moduleIdStr: string) => {
    if (moduleIdStr === 'all') {
      onChange({ targetType: MiniBannerTargetType.COURSE, targetId: selectedCourseId });
      return;
    }
    const moduleId = parseInt(moduleIdStr, 10);
    onChange({ targetType: MiniBannerTargetType.MODULE, targetId: moduleId });
  };

  const handleItemChange = (itemIdStr: string) => {
    if (itemIdStr === 'all') {
      onChange({ targetType: MiniBannerTargetType.MODULE, targetId: currentModuleId });
      return;
    }
    const itemId = parseInt(itemIdStr, 10);
    onChange({ targetType: MiniBannerTargetType.MODULE_ITEM, targetId: itemId });
  };

  const currentItemId = target.targetType === MiniBannerTargetType.MODULE_ITEM ? target.targetId : 0;

  const targetLabel = getTargetLabel(target);
  const targetScope = target.targetType === MiniBannerTargetType.LIVESTREAM
    ? MiniBannerTargetType.LIVESTREAM
    : MiniBannerTargetType.COURSE;

  const handleTargetScopeChange = (nextType: string) => {
    if (nextType === MiniBannerTargetType.LIVESTREAM) {
      setSelectedCourseId(0);
      onChange({ targetType: MiniBannerTargetType.LIVESTREAM, targetId: 0 });
      return;
    }
    onChange({ targetType: MiniBannerTargetType.COURSE, targetId: selectedCourseId });
  };

  const handleLivestreamChange = (livestreamIdStr: string) => {
    const livestreamId = parseInt(livestreamIdStr, 10);
    onChange({ targetType: MiniBannerTargetType.LIVESTREAM, targetId: livestreamId });
  };

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {TARGET_TYPE_LABELS[target.targetType]}: {targetLabel}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <Label>Тип цели</Label>
          <Select value={targetScope} onValueChange={handleTargetScopeChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={MiniBannerTargetType.COURSE}>Курс / модуль / урок</SelectItem>
              <SelectItem value={MiniBannerTargetType.LIVESTREAM}>Трансляция</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {targetScope === MiniBannerTargetType.LIVESTREAM ? (
          <div className="md:col-span-2">
            <Label>Трансляция</Label>
            <Select
              value={target.targetId > 0 ? String(target.targetId) : ''}
              onValueChange={handleLivestreamChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите трансляцию..." />
              </SelectTrigger>
              <SelectContent>
                {livestreams.map((stream) => (
                  <SelectItem key={stream.id} value={String(stream.id)}>
                    {getLivestreamDisplayName(stream)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
        {/* Course */}
        <div>
          <Label>Курс</Label>
          <Select
            value={selectedCourseId > 0 ? String(selectedCourseId) : ''}
            onValueChange={handleCourseChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Выберите курс..." />
            </SelectTrigger>
            <SelectContent>
              {courses.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {getRuTitle(c.translations) ?? c.title ?? c.slug ?? `#${c.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Module */}
        <div>
          <Label>Модуль</Label>
          <Select
            value={currentModuleId > 0 ? String(currentModuleId) : 'all'}
            onValueChange={handleModuleChange}
            disabled={selectedCourseId === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="Выберите модуль..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все модули (весь курс)</SelectItem>
              {modules.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {getRuTitle(m.translations) ?? `Модуль #${m.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Module Item */}
        <div>
          <Label>Урок</Label>
          <Select
            value={currentItemId > 0 ? String(currentItemId) : 'all'}
            onValueChange={handleItemChange}
            disabled={currentModuleId === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="Выберите урок..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все уроки модуля</SelectItem>
              {items.map((i) => (
                <SelectItem key={i.id} value={String(i.id)}>
                  {getRuTitle(i.translations) ?? `Урок #${i.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
